import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { fmtCurrency, fmtCurrencyCompact } from '../lib/format.ts';
import { COLORS, axisStyle, tooltipStyle } from '../lib/echarts-theme.ts';
import { useIsDark } from '../lib/useIsDark.ts';
import { ChartSkeleton } from '../components/Skeleton.tsx';

const REGIONS_LABEL: Record<string, string> = {
  '0': 'PLZ 0 — Sachsen, Thüringen',
  '1': 'PLZ 1 — Berlin, Brandenburg',
  '2': 'PLZ 2 — Hamburg, Bremen',
  '3': 'PLZ 3 — Niedersachsen, Hessen N.',
  '4': 'PLZ 4 — NRW Nord',
  '5': 'PLZ 5 — NRW Süd, Rheinland',
  '6': 'PLZ 6 — Hessen, Saarland',
  '7': 'PLZ 7 — Baden-Württemberg',
  '8': 'PLZ 8 — Bayern Süd',
  '9': 'PLZ 9 — Bayern Nord, Sachsen',
};

export function GeoChart() {
  const dark = useIsDark();
  const filters = useFilterStore((s) => s.filters);
  const addFilter = useFilterStore((s) => s.addFilter);
  const { data, isLoading } = useQuery({
    queryKey: ['geo', filters, 'bundesland'],
    queryFn: () => api.geo({ filters, level: 'bundesland' }),
  });

  if (isLoading) return <ChartSkeleton height={320} />;

  const sorted = [...(data ?? [])].sort((a, b) => b.umsatz - a.umsatz).reverse();
  const labels = sorted.map((r) => REGIONS_LABEL[r.region] ?? `PLZ ${r.region}`);
  const values = sorted.map((r) => r.umsatz);
  const max = Math.max(...values, 1);

  const option = {
    grid: { top: 10, right: 60, bottom: 24, left: 4, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: Array<{ name: string; value: number }>) => {
        const p = params[0];
        const pct = (p.value / values.reduce((a, b) => a + b, 0)) * 100;
        return `<div style="font-size:12px">
          <div style="font-weight:600;margin-bottom:4px">${p.name}</div>
          <div style="font-variant-numeric:tabular-nums">${fmtCurrency(p.value)}</div>
          <div style="color:#94a3b8;font-size:11px;margin-top:2px">${pct.toFixed(1)}% Anteil</div>
        </div>`;
      },
    },
    xAxis: {
      type: 'value',
      max,
      ...axisStyle(),
      axisLabel: { ...axisStyle().axisLabel, formatter: (v: number) => fmtCurrencyCompact(v) },
    },
    yAxis: {
      type: 'category',
      data: labels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: dark ? COLORS.darkText : COLORS.text, fontSize: 11, fontFamily: 'Inter, sans-serif' },
    },
    series: [
      {
        type: 'bar',
        data: values,
        barCategoryGap: '35%',
        itemStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 1, y2: 0,
            colorStops: [
              { offset: 0, color: '#8b5cf6' },
              { offset: 1, color: '#a78bfa' },
            ],
          },
          borderRadius: [0, 4, 4, 0],
        },
        label: {
          show: true,
          position: 'right',
          formatter: (p: { value: number }) => fmtCurrencyCompact(p.value),
          fontSize: 11,
          color: dark ? COLORS.darkText : COLORS.text,
          fontWeight: 600,
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 320 }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
      onEvents={{
        click: (params: { dataIndex: number }) => {
          const region = sorted[params.dataIndex];
          if (region) addFilter('plz', String(region.region).substring(0, 1));
        },
      }}
    />
  );
}
